// Recurrence: the pure date math, and the series payloads.
//
// Port of app/core/functions/recurrence.php. Read migration-plan.md §5 before
// changing anything here — that behaviour was worked out across three sessions,
// including an eager generator that was BUILT AND THEN DELIBERATELY REMOVED. The
// storage model is not "expand on the fly" and not "pre-generate a window"; it is
// a third thing:
//
//   LAZY, SCHEDULE-TRIGGERED MATERIALIZATION. A series is a template plus a rule.
//   Occurrences are REAL `events` rows, because an occurrence carries RSVPs, chat,
//   photos, and results. But no row exists until its trigger fires.
//
// Every function in this file is PURE — no database, no clock except what is
// passed in. That is deliberate: this is the highest-risk logic in the codebase,
// and it is unit-tested in `shared/test/recurrence.test.ts` independently of any
// DB path. Keep it that way. If a change here needs a query, the query belongs in
// the caller.
//
// All date arithmetic runs in UTC on bare 'yyyy-MM-dd' values. A recurrence date
// is a CALENDAR LABEL, not an instant: "every Thursday" means the same local
// Thursday regardless of DST. Doing this maths in a real zone is how a weekly 7 PM
// series drifts an hour twice a year.
import { DateTime, eventInstant, resolveEventTz, TimeFormat } from './time.js';
export const RECURRENCE_TYPES = [
    'daily',
    'weekly',
    'monthly',
    'yearly',
    'custom',
];
/** Monday-first, matching Luxon's `weekday` (1 = Monday … 7 = Sunday). */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const WEEKDAY_LABELS = {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
};
/**
 * Defensive cap on iterations, against pathological data — a daily series with a
 * ten-year window, say. Carried over from the PHP original at the same value.
 * Hitting it truncates the result rather than hanging; callers ask for bounded
 * windows, so in practice it is never reached.
 */
export const RECURRENCE_MAX_ITER = 4000;
/** Custom intervals are clamped to this many days. */
export const MAX_CUSTOM_INTERVAL = 365;
/** `feed_post_days_before` is clamped to this range. */
export const MAX_DAYS_BEFORE = 30;
/**
 * Parse a "mon,wed,thu" CSV into a set. Unknown tokens are dropped rather than
 * rejected, so one typo in stored data does not break the whole series.
 *
 * The set iterates MONDAY-FIRST regardless of the input order. A JS Set iterates
 * in insertion order, so building it from the input directly would make
 * `recurrenceLabel` render "Repeats weekly on Thu, Mon" for a series stored as
 * "thu,mon" — which is what the PHP original did. Canonicalising here fixes every
 * consumer at once instead of asking each to re-sort.
 */
export function parseWeekdaySet(csv) {
    const out = new Set();
    if (!csv)
        return out;
    const seen = new Set();
    for (const raw of csv.toLowerCase().split(','))
        seen.add(raw.trim());
    for (const day of WEEKDAYS) {
        if (seen.has(day))
            out.add(day);
    }
    return out;
}
/** Serialize a weekday set back to CSV, always in Monday-first order. */
export function formatWeekdaySet(days) {
    const set = new Set(days);
    return WEEKDAYS.filter((d) => set.has(d)).join(',');
}
/** Human description for badges and the series list. Port of recurrenceLabel(). */
export function recurrenceLabel(rule) {
    if (rule.type === 'daily')
        return 'Repeats daily';
    if (rule.type === 'weekly') {
        const picked = [...parseWeekdaySet(rule.days)].map((d) => WEEKDAY_LABELS[d]);
        return picked.length ? `Repeats weekly on ${picked.join(', ')}` : 'Repeats weekly';
    }
    if (rule.type === 'monthly')
        return 'Repeats monthly';
    if (rule.type === 'yearly')
        return 'Repeats yearly';
    if (rule.type === 'custom') {
        const n = Math.max(1, rule.interval ?? 0);
        return n === 1 ? 'Repeats daily' : `Repeats every ${n} days`;
    }
    return 'Recurring';
}
// --- Date helpers (zone-free) --------------------------------------------
/** Parse a bare 'yyyy-MM-dd' as a calendar label. UTC, so no zone can shift it. */
function day(ymd) {
    return DateTime.fromFormat(ymd, 'yyyy-MM-dd', { zone: 'utc' });
}
function ymd(dt) {
    return dt.toFormat(TimeFormat.SQL_DATE);
}
/** Add or subtract whole days from a 'yyyy-MM-dd'. Empty string if unparseable. */
export function shiftYmd(date, days) {
    const dt = day(date);
    return dt.isValid ? ymd(dt.plus({ days })) : '';
}
// --- The core: which dates does this rule produce? -----------------------
/**
 * Every occurrence date for `rule` within [fromYmd, toYmd], inclusive.
 *
 * The window is clamped to the series' own bounds first:
 *   rangeStart = max(startDate, fromYmd)
 *   rangeEnd   = min(endDate, toYmd)   (endDate optional)
 * An inverted range yields nothing.
 *
 * Returns 'yyyy-MM-dd' strings in ascending order. Pure: no DB, no clock.
 *
 * Per-type rules, all carried over exactly:
 *
 *  daily    every day in range.
 *  weekly   only the weekdays in `days`. THE ANCHOR WEEKDAY IS NOT IMPLIED — a
 *           series anchored on a Monday with days="wed,thu" produces no Mondays.
 *           An empty set produces nothing at all, rather than defaulting to the
 *           anchor's weekday, because guessing here would silently create events.
 *  monthly  the anchor's day-of-month, CLAMPED to each month's length. Anchored
 *           on the 31st gives Feb 28 (29 in a leap year) and Apr 30.
 *  yearly   the anchor's month and day each year. Feb 29 clamps to Feb 28 in
 *           non-leap years.
 *  custom   every N days from the anchor, jumping straight to the first
 *           occurrence >= rangeStart rather than walking from the anchor.
 */
export function recurrenceOccurrenceDates(rule, fromYmd, toYmd) {
    if (!rule.startDate)
        return [];
    // String comparison is correct for zero-padded ISO dates, and avoids parsing
    // three values just to order them.
    const rangeStart = rule.startDate > fromYmd ? rule.startDate : fromYmd;
    let rangeEnd = toYmd;
    if (rule.endDate && rule.endDate < rangeEnd)
        rangeEnd = rule.endDate;
    if (rangeStart > rangeEnd)
        return [];
    const start = day(rule.startDate);
    const rs = day(rangeStart);
    const re = day(rangeEnd);
    if (!start.isValid || !rs.isValid || !re.isValid)
        return [];
    const dates = [];
    let guard = 0;
    switch (rule.type) {
        case 'daily': {
            let cur = rs;
            while (cur <= re && guard++ < RECURRENCE_MAX_ITER) {
                dates.push(ymd(cur));
                cur = cur.plus({ days: 1 });
            }
            break;
        }
        case 'weekly': {
            const days = parseWeekdaySet(rule.days);
            // No selected weekdays means no occurrences. Deliberate: falling back to
            // the anchor's weekday would create events nobody asked for.
            if (days.size === 0)
                break;
            let cur = rs;
            while (cur <= re && guard++ < RECURRENCE_MAX_ITER) {
                // Luxon weekday: 1 = Monday … 7 = Sunday, matching WEEKDAYS.
                if (days.has(WEEKDAYS[cur.weekday - 1]))
                    dates.push(ymd(cur));
                cur = cur.plus({ days: 1 });
            }
            break;
        }
        case 'monthly': {
            const anchorDom = start.day;
            // Walk month by month from the FIRST OF THE MONTH, so `plus({months: 1})`
            // never has to clamp and the sequence of months stays exact.
            //
            // The PHP original started at the anchor's month and filtered; starting at
            // rangeStart's month instead is equivalent (every earlier month yields a
            // date before rangeStart, which the filter drops anyway) and avoids walking
            // years of months for a window years after the anchor.
            let cur = rs.startOf('month');
            while (cur <= re && guard++ < RECURRENCE_MAX_ITER) {
                const dom = Math.min(anchorDom, cur.daysInMonth);
                const occ = ymd(cur.set({ day: dom }));
                if (occ >= rangeStart && occ <= rangeEnd)
                    dates.push(occ);
                cur = cur.plus({ months: 1 });
            }
            break;
        }
        case 'yearly': {
            const anchorMonth = start.month;
            const anchorDom = start.day;
            for (let y = rs.year; y <= re.year && guard++ < RECURRENCE_MAX_ITER; y++) {
                const firstOfMonth = DateTime.fromObject({ year: y, month: anchorMonth, day: 1 }, { zone: 'utc' });
                // Feb 29 anchors clamp to Feb 28 in a non-leap year.
                const dom = Math.min(anchorDom, firstOfMonth.daysInMonth);
                const occ = ymd(firstOfMonth.set({ day: dom }));
                if (occ >= rangeStart && occ <= rangeEnd)
                    dates.push(occ);
            }
            break;
        }
        case 'custom': {
            const interval = Math.max(1, rule.interval ?? 0);
            // Jump straight to the first occurrence at or after rangeStart instead of
            // stepping from the anchor, which for a distant window would burn the whole
            // iteration guard before producing anything.
            const daysDiff = Math.round(rs.diff(start, 'days').days);
            const k = daysDiff > 0 ? Math.ceil(daysDiff / interval) : 0;
            let occ = start.plus({ days: k * interval });
            while (occ <= re && guard++ < RECURRENCE_MAX_ITER) {
                const label = ymd(occ);
                if (label >= rangeStart)
                    dates.push(label);
                occ = occ.plus({ days: interval });
            }
            break;
        }
    }
    return dates;
}
// --- The trigger ---------------------------------------------------------
//
//   trigger = (occurrence_date − feed_post_days_before) at feed_post_time
//
// interpreted in the SERIES' AUTHORING ZONE. `feedPostTime` null means the series
// never auto-materializes; occurrences come only from the manual button.
/** The calendar date the trigger falls on. 'yyyy-MM-dd'. */
export function seriesTriggerDate(occDate, daysBefore) {
    return shiftYmd(occDate, -Math.max(0, daysBefore));
}
/**
 * The trigger as a NAIVE 'yyyy-MM-dd HH:mm:ss' string. **UI display only.**
 *
 * Port of seriesPostDatetime(). Never compare this to a clock — it carries no
 * zone, so a series authored outside the site zone would fire at the wrong
 * absolute moment. Use `seriesTriggerInstant()` for the due check.
 */
export function seriesTriggerNaive(occDate, daysBefore, feedPostTime) {
    if (!feedPostTime)
        return null;
    const date = seriesTriggerDate(occDate, daysBefore);
    if (!date)
        return null;
    const time = feedPostTime.length === 5 ? `${feedPostTime}:00` : feedPostTime;
    return `${date} ${time}`;
}
/**
 * The trigger as a REAL INSTANT, resolved in the series' authoring zone.
 *
 * Port of seriesPostInstant(). **This is the one the due check uses**, compared
 * against a real clock. The naive counterpart above exists only for display, and
 * comparing that string to a clock is the bug this pairing exists to prevent: a
 * series authored in Tokyo must materialize at Tokyo's 9 AM, not the site's.
 *
 * Returns null when the series has no post time, which means "never
 * auto-materialize" rather than "due now".
 */
export function seriesTriggerInstant(occDate, daysBefore, feedPostTime, zones) {
    if (!feedPostTime)
        return null;
    const date = seriesTriggerDate(occDate, daysBefore);
    if (!date)
        return null;
    const zone = resolveEventTz({
        eventZone: zones.seriesZone,
        groupZone: zones.groupZone,
        siteZone: zones.siteZone,
    });
    return eventInstant(date, feedPostTime, zone);
}
/**
 * Has this occurrence's trigger arrived? `nowMs` is injected so this is testable
 * and so a single cron tick uses one consistent clock across every series.
 *
 * A series with no post time is never due — it is manual-only.
 */
export function isTriggerDue(occDate, daysBefore, feedPostTime, zones, nowMs) {
    const instant = seriesTriggerInstant(occDate, daysBefore, feedPostTime, zones);
    return instant !== null && instant.toMillis() <= nowMs;
}
/**
 * The fields a materialized occurrence inherits from its series.
 *
 * Documented as data because the exclusions matter as much as the inclusions:
 * `min_met_notified_at`, `full_notified_at`, `feed_posted_at`, and `closed_at`
 * are deliberately absent, so every occurrence fires its OWN lifecycle hooks and
 * gets its own announcement. Inheriting a stamp would mean the second poker night
 * never announces "Game on!" because the first one already did.
 */
const WEEKDAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
/**
 * Replace `{day}` with the weekday of an occurrence's date: "{day} Night Poker"
 * on a Tuesday is "Tuesday Night Poker". Case-insensitive, so `{Day}` works too.
 *
 * The weekday comes from the date string alone ('yyyy-MM-dd'), read as a UTC
 * calendar day. A series stores a naive wall-clock date in its own zone, and the
 * weekday of a calendar date does not depend on a zone, so there is no instant to
 * convert and no way to land on the wrong day. A date that does not parse is
 * returned untouched rather than throwing, since this runs inside cron.
 */
export function expandDayToken(text, ymd) {
    if (!/\{day\}/i.test(text))
        return text;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m)
        return text;
    const dow = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
    return text.replace(/\{day\}/gi, WEEKDAY_NAMES[dow] ?? '');
}
export const SERIES_INHERITED_FIELDS = [
    'title',
    'description',
    'feed_message',
    'start_time',
    'end_time',
    'location',
    'loc_address',
    'loc_city',
    'loc_state',
    'loc_zip',
    'loc_phone',
    'organizer_phone',
    'rsvp_min',
    'rsvp_max',
    'headcount_lead_minutes',
    'feed_post_days_before',
    'feed_post_time',
    'feed_notify',
    'event_type',
    'type_config',
    'type_messages',
    'time_zone',
    'created_by',
];
//# sourceMappingURL=recurrence.js.map